import { AtSign, Frown, MapPin, Target, Users, X } from 'lucide-react';

import type { Persona, PersonaTechSavviness } from '@repo/contracts';

import { PersonaAvatar } from '../persona/persona-avatar';

const COMFORT: Record<PersonaTechSavviness, { label: string; level: number }> =
  {
    high: { label: 'High', level: 3 },
    low: { label: 'Low', level: 1 },
    medium: { label: 'Medium', level: 2 },
  };

function Facts({
  items,
  label,
  tone,
}: {
  items: string[];
  label: string;
  tone: 'frustration' | 'goal';
}) {
  if (items.length === 0) return null;
  const goal = tone === 'goal';
  return (
    <section
      className={`rounded-card px-3.5 py-3 ${goal ? 'bg-tag-success' : 'bg-tag-critical'}`}
    >
      <p
        className={`flex items-center gap-1.5 text-caption font-semibold ${goal ? 'text-success-ink' : 'text-critical'}`}
      >
        {goal ? (
          <Target aria-hidden="true" className="size-3.5" />
        ) : (
          <Frown aria-hidden="true" className="size-3.5" />
        )}
        {label}
      </p>
      <ul className="mt-2 space-y-1.5">
        {items.slice(0, 3).map((item) => (
          <li
            className="flex gap-2 text-caption leading-snug text-text-primary"
            key={item}
          >
            <span
              aria-hidden="true"
              className={`mt-1.5 size-1 shrink-0 rounded-full ${goal ? 'bg-success-ink' : 'bg-critical'}`}
            />
            {item}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Who someone in the room is, at a glance — what a researcher glances at
 * before asking them something, not the full profile the panel modal shows.
 * Three goals and three frustrations at most: the card is a reminder, and the
 * modal is one click away for everything else.
 */
export function PersonaCard({
  onAsk,
  onClose,
  onLookalikes,
  persona,
}: {
  /** Starts a question to just this person. Absent before a room exists. */
  onAsk?: () => void;
  onClose: () => void;
  /** Opens the Lookalikes explorer for this persona. */
  onLookalikes?: () => void;
  persona: Persona;
}) {
  // Keyed by the person in the parent, so switching people replays the
  // entrance instead of swapping text under the reader's eyes.
  const demographics = persona.demographics;
  const where = demographics
    ? [String(demographics.age), demographics.location]
        .filter(Boolean)
        .join(' · ')
    : '';
  const comfort = COMFORT[persona.techSavviness];

  return (
    // A column: who they are on top, the rest scrolling under it, and the
    // ways to ask them pinned at the foot — never cut off by the screen.
    <div className="flex max-h-full min-h-0 flex-col overflow-hidden rounded-card bg-surface shadow-popover ring-1 ring-hairline duration-200 animate-in fade-in slide-in-from-right-2 motion-reduce:animate-none">
      <div className="flex shrink-0 items-start gap-3 px-5 pt-5 pb-4">
        <PersonaAvatar persona={persona} size="xl" />
        <div className="min-w-0 flex-1">
          <p className="text-heading-xs leading-tight font-semibold text-text-primary">
            {persona.name}
          </p>
          <p className="mt-1 text-caption leading-snug font-medium text-text-secondary">
            {persona.title}
          </p>
          {demographics?.occupation ? (
            <p className="mt-1.5 text-tag leading-snug text-text-muted">
              {demographics.occupation}
            </p>
          ) : null}
          {where ? (
            <p className="flex items-center gap-1 text-tag leading-snug text-text-muted">
              <MapPin aria-hidden="true" className="size-3 shrink-0" />
              {where}
            </p>
          ) : null}
        </div>
        <button
          aria-label={`Close ${persona.name}'s profile`}
          className="-mt-1 -mr-1 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-pill text-text-secondary transition hover:bg-surface-hover hover:text-text-primary"
          onClick={onClose}
          type="button"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto border-t border-hairline px-5 pt-4 pb-5">
        <p className="mb-1 text-caption leading-relaxed text-text-secondary">
          {persona.description}
        </p>

        {persona.experiences?.length ? (
          <section>
            <p className="text-caption font-semibold text-text-primary">
              Their situation
            </p>
            <ul className="mt-1.5 space-y-1">
              {persona.experiences.map((item) => (
                <li
                  className="relative pl-3.5 text-caption leading-snug text-text-secondary before:absolute before:top-[0.55em] before:left-0 before:size-1 before:rounded-full before:bg-text-muted"
                  key={item}
                >
                  {item}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Side by side: wide enough for each to read as a list, and the
            card stays short enough to take in without scrolling. */}
        <div className="grid grid-cols-2 gap-3">
          <Facts items={persona.goals} label="Wants" tone="goal" />
          <Facts
            items={persona.frustrations}
            label="Frustrated by"
            tone="frustration"
          />
        </div>

        <div className="mt-1 flex items-center justify-between px-0.5">
          <p className="text-caption text-text-secondary">Tech comfort</p>
          <p className="flex items-center gap-2 text-caption font-semibold text-text-primary">
            <span aria-hidden="true" className="flex gap-0.5">
              {[1, 2, 3].map((step) => (
                <span
                  className={`size-1.5 rounded-full ${step <= comfort.level ? 'bg-text-primary' : 'bg-hairline-alt'}`}
                  key={step}
                />
              ))}
            </span>
            {comfort.label}
          </p>
        </div>
      </div>

      {onAsk || onLookalikes ? (
        <div className="flex shrink-0 gap-2 border-t border-hairline bg-surface px-5 py-3.5">
          {onAsk ? (
            <button
              className="flex h-10 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-button bg-ink text-ui-strong font-semibold text-white transition hover:bg-ink/90 active:scale-[0.98]"
              onClick={onAsk}
              type="button"
            >
              <AtSign aria-hidden="true" className="size-4" />
              Ask {persona.name.split(' ')[0]}
            </button>
          ) : null}
          {onLookalikes ? (
            <button
              className={[
                'flex h-10 cursor-pointer items-center justify-center gap-1.5 rounded-button px-3.5 text-ui-strong font-semibold text-text-secondary ring-1 ring-hairline-alt transition hover:bg-warm-alt hover:text-text-primary active:scale-[0.98]',
                onAsk ? '' : 'flex-1',
              ].join(' ')}
              onClick={onLookalikes}
              type="button"
            >
              <Users aria-hidden="true" className="size-4" />
              Lookalikes
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
